// src/components/MemoryUploadForm.jsx
import React, { useState } from "react";
import { supabase } from "../supabaseClient";

export default function MemoryUploadForm() {
  const [name, setName] = useState("");
  const [anon, setAnon] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileCaptions, setFileCaptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");

  // When files are selected
  function handleFileChange(e) {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setFileCaptions(selectedFiles.map(() => ""));
  }

  // Caption change for a specific file
  function handleCaptionChange(idx, value) {
    setFileCaptions((prev) => {
      const updated = [...prev];
      updated[idx] = value;
      return updated;
    });
  }

  // Remove file from selection
  function handleRemoveFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFileCaptions((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("");
    if (!name.trim() && !anon) {
      setStatus("Please enter your name or post as anonymous.");
      return;
    }
    if (files.length === 0) {
      setStatus("Please select at least one photo or video.");
      return;
    }

    setUploading(true);
    setStatus("Uploading...");

    // Upload and insert each file as a separate submission
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const caption = fileCaptions[i] || "";

      const ext = file.name.split(".").pop();
      const uniqueName = `${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("theyda")
        .upload(uniqueName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        setUploading(false);
        setStatus("Upload failed: " + uploadError.message);
        return;
      }

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from("theyda")
        .getPublicUrl(uniqueName);

      // Insert new row for each file
      const { error: tableError } = await supabase
        .from("gallery-submissions")
        .insert([
          {
            name: name.trim(),
            publicName: anon ? "Anonymous" : name.trim(),
            isAnonymous: anon,
            caption: caption.trim(),
            files: [{
              url: publicUrlData.publicUrl,
              type: file.type.startsWith("image") ? "image" : "video",
              name: file.name,
            }],
            created_at: new Date().toISOString(),
            status: "pending",
          },
        ]);
      if (tableError) {
        setUploading(false);
        setStatus("Error saving your submission: " + tableError.message);
        return;
      }
    }

    setUploading(false);
    setStatus("Submitted! Thank you for sharing.");
    setName("");
    setAnon(false);
    setFiles([]);
    setFileCaptions([]);
    setTimeout(() => setStatus(""), 3200);
  }

  // Display image/video preview
  function getPreview(file) {
    if (file.type.startsWith("image")) {
      return (
        <img
          src={URL.createObjectURL(file)}
          alt="preview"
          style={{
            width: 80,
            height: 80,
            objectFit: "cover",
            borderRadius: 8,
            boxShadow: "0 2px 6px #cbbaf7",
            marginBottom: 8
          }}
        />
      );
    } else if (file.type.startsWith("video")) {
      return (
        <video width={80} height={80} controls style={{ borderRadius: 8, marginBottom: 8 }}>
          <source src={URL.createObjectURL(file)} />
          Your browser does not support video.
        </video>
      );
    }
    return null;
  }

  return (
    <form className="guestbook-form" onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
      <label>
        Your Name
        <input
          type="text"
          value={name}
          disabled={anon}
          onChange={e => setName(e.target.value)}
          required={!anon}
        />
      </label>
      <label style={{ display: "flex", alignItems: "center", fontWeight: 600, color: "#6d3dc6" }}>
        <input
          type="checkbox"
          checked={anon}
          onChange={e => setAnon(e.target.checked)}
          style={{ marginRight: 8 }}
        />
        Post as anonymous to the public
      </label>
      <label>
        Select Photo(s) or Video(s):
        <input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileChange}
          disabled={uploading}
        />
      </label>
      {files.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 24, margin: "18px 0 12px 0"
        }}>
          {files.map((file, idx) => (
            <div key={idx} style={{
              background: "#f6f0fe",
              border: "1px solid #dacbfa",
              borderRadius: 12,
              padding: 12,
              minWidth: 125,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative"
            }}>
              {getPreview(file)}
              <input
                type="text"
                placeholder="Caption (optional)"
                value={fileCaptions[idx]}
                onChange={e => handleCaptionChange(idx, e.target.value)}
                style={{
                  border: "1px solid #d1c4e9",
                  borderRadius: 6,
                  padding: "5px 8px",
                  width: 110,
                  marginBottom: 6,
                  fontSize: 14,
                  background: "#fff"
                }}
                maxLength={200}
              />
              <button type="button"
                onClick={() => handleRemoveFile(idx)}
                style={{
                  position: "absolute",
                  top: 4, right: 8,
                  background: "none",
                  color: "#aa66e6",
                  border: "none",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontSize: 18
                }}
                aria-label="Remove"
                title="Remove"
              >×</button>
            </div>
          ))}
        </div>
      )}
      <button type="submit" disabled={uploading} style={{ marginTop: 10 }}>
        {uploading ? "Uploading..." : "Submit Memory"}
      </button>
      {status && (
        <div style={{ marginTop: 10, color: status.includes("fail") || status.includes("Error") ? "crimson" : "#6d3dc6" }}>
          {status}
        </div>
      )}
    </form>
  );
}
